import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { FamilyState } from "@reducers/types/HRM/family-state";

import {
  getFamilyRequest,
  updateParentInfoRequest,
  updateSpouseInfoRequest,
  updateChildrenInfoRequest,
  updatePermanentAddressInfoRequest,
  updatePresentAddressInfoRequest,
  deleteChildInfoRequest,
  deleteSpouseInfoRequest,
} from "@actions/HRM/employee/family-actions";
import { getFamilyState } from "@selectors/HRM/employee/family-info-selector";

import { useAppSelector } from "@helpers/redux";
import {
  FamilyType,
  ParentType,
  SpouseType,
  ChildType,
  PermanentAddressType,
  PresentAddressType,
  RationType,
} from "@type/HRM/family";
import { getRation } from "@api/family";

interface UseFamily {
  getFamilyInfoRequestOngoing: boolean;

  updateParentRequestOngoing: boolean;
  updateParentSuccess: boolean;
  updateParentFailed: boolean;

  updateSpouseRequestOngoing: boolean;
  updateSpouseSuccess: boolean;
  updateSpouseFailed: boolean;

  updatePermanentAddressRequestOngoing: boolean;
  updatePermanentAddressSuccess: boolean;
  updatePermanentAddressFailed: boolean;

  updatePresentAddressRequestOngoing: boolean;
  updatePresentAddressSuccess: boolean;
  updatePresentAddressFailed: boolean;

  updateChildRequestOngoing: boolean;
  updateChildSuccess: boolean;
  updateChildFailed: boolean;

  deleteChildRequestOngoing: boolean;
  deleteChildSuccess: boolean;
  deleteChildFailed: boolean;

  deleteSpouseRequestOngoing: boolean;
  deleteSpouseSuccess: boolean;
  deleteSpouseFailed: boolean;

  familyInfo: FamilyType;

  changeParentInfo: (updateParentInfo: ParentType) => void;
  changeSpouseInfo: (updateSpouseInfo: SpouseType) => void;
  changeChildrenInfo: (updateChildInfo: ChildType) => void;
  changePermanentAddressInfo: (
    updatePermanentAddressInfo: PermanentAddressType,
  ) => void;
  changePresentAddressInfo: (
    updatePresentAddressInfo: PresentAddressType,
  ) => void;

  removeChildInfo: (childID: number) => void;
  removeSpouseInfo: (spouseID: number) => void;

  ration: RationType | null;
  refetchRationData: (employeeID: number | null) => void;
}

export const useFamilyInfo = (id: number | null): UseFamily => {
  const dispatch = useDispatch();
  const [ration, setRation] = useState<any>(null);
  const {
    data,

    getFamilyInfoRequestOngoing,

    updateParentRequestOngoing,
    updateParentSuccess,
    updateParentFailed,

    updateSpouseRequestOngoing,
    updateSpouseSuccess,
    updateSpouseFailed,

    updatePermanentAddressRequestOngoing,
    updatePermanentAddressSuccess,
    updatePermanentAddressFailed,

    updatePresentAddressRequestOngoing,
    updatePresentAddressSuccess,
    updatePresentAddressFailed,

    updateChildRequestOngoing,
    updateChildSuccess,
    updateChildFailed,

    deleteChildRequestOngoing,
    deleteChildSuccess,
    deleteChildFailed,

    deleteSpouseRequestOngoing,
    deleteSpouseSuccess,
    deleteSpouseFailed,
  } = useAppSelector<FamilyState>(getFamilyState);

  // GET
  useEffect(() => {
    if (id) {
      dispatch(getFamilyRequest(id));
      getRation(id)
        .then((response) => {
          setRation(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching ration data:", error);
        });
    } //Here id is employee id
  }, [dispatch, id]);
  // Refetch ration data
  const refetchRationData = (employeeID: number | null) => {
    if (employeeID) {
      getRation(employeeID)
        .then((response) => {
          setRation(response.data.data);
        })
        .catch((error) => {
          console.error("Error fetching ration data:", error);
        });
    }
  };

  const family = data;

  //UPDATE PARENT INFO
  const changeParentInfo = (updateParentInfo: ParentType) => {
    return dispatch(
      updateParentInfoRequest({ employeeID: id, ...updateParentInfo }),
    );
  };

  //UPDATE SPOUSE INFO
  const changeSpouseInfo = (updateSpouseInfo: SpouseType) => {
    return dispatch(
      updateSpouseInfoRequest({ employeeID: id, ...updateSpouseInfo }),
    );
  };

  //UPDATE CHILDREN INFO
  const changeChildrenInfo = (updateChildInfo: ChildType) =>
    dispatch(
      updateChildrenInfoRequest({
        employeeID: id,
        ...updateChildInfo,
      } as ChildType),
    );

  //UPDATE PERMANENT ADDRESS INFO
  const changePermanentAddressInfo = (
    updatePermanentAddressInfo: PermanentAddressType,
  ) =>
    dispatch(
      updatePermanentAddressInfoRequest({
        employeeID: id,
        ...updatePermanentAddressInfo,
      } as PermanentAddressType),
    );

  //UPDATE PRESENT ADDRESS INFO
  const changePresentAddressInfo = (
    updatePresentAddressInfo: PresentAddressType,
  ) =>
    dispatch(
      updatePresentAddressInfoRequest({
        employeeID: id,
        ...updatePresentAddressInfo,
      } as PresentAddressType),
    );

  //DELETE CHILD INFO
  const removeChildInfo = (childID: number) =>
    dispatch(
      deleteChildInfoRequest({
        employeeID: id,
        childID,
      }),
    );

  //DELETE SPOUSE INFO
  const removeSpouseInfo = (spouseID: number) =>
    dispatch(
      deleteSpouseInfoRequest({
        employeeID: id,
        spouseID,
      }),
    );

  return {
    getFamilyInfoRequestOngoing,

    updateParentRequestOngoing,
    updateParentSuccess,
    updateParentFailed,

    updateSpouseRequestOngoing,
    updateSpouseSuccess,
    updateSpouseFailed,

    updatePermanentAddressRequestOngoing,
    updatePermanentAddressSuccess,
    updatePermanentAddressFailed,

    updatePresentAddressRequestOngoing,
    updatePresentAddressSuccess,
    updatePresentAddressFailed,

    updateChildRequestOngoing,
    updateChildSuccess,
    updateChildFailed,

    deleteChildRequestOngoing,
    deleteChildSuccess,
    deleteChildFailed,

    deleteSpouseRequestOngoing,
    deleteSpouseSuccess,
    deleteSpouseFailed,

    familyInfo: family as FamilyType,

    changeParentInfo,
    changeSpouseInfo,
    changeChildrenInfo,
    changePermanentAddressInfo,
    changePresentAddressInfo,

    removeChildInfo,
    removeSpouseInfo,

    ration,
    refetchRationData,
  };
};